/**
 * The descent.
 *
 * One number, t ∈ [0,1], from the top landing to the square. The wheel, a
 * drag, the arrow keys and a slow gravity all push it; a spring smooths it so
 * the traveller is never jolted. Everything else in Isidora is a function of
 * this number, which is why the whole city ages together.
 */
export class Journey {
  /**
   * @param {HTMLElement} element
   * @param {object} settings `settings.journey`
   * @param {import('../core/Bus.js').Bus} bus
   */
  constructor(element, settings, bus) {
    this.el = element;
    this.cfg = settings;
    this.bus = bus;

    this.target = 0; // where the traveller intends to be
    this.value = 0; // where the body actually is
    this.velocity = 0; // d(value)/dt, used for sway and sound
    this.impulse = 0; // decaying push from the wheel
    this.drifting = true; // the stair pulls until you take hold of it
    this.arrived = false;
    this.keys = new Set();

    this._dragging = false;
    this._lastY = 0;
    this._pointerId = null;
    this._bound = [];
    this._attach();
  }

  _on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this._bound.push(() => target.removeEventListener(type, fn, opts));
  }

  _attach() {
    this._on(
      this.el,
      'wheel',
      (e) => {
        e.preventDefault();
        this.drifting = false;
        this.impulse += e.deltaY * this.cfg.wheelScale;
      },
      { passive: false },
    );

    this._on(this.el, 'pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this._dragging = true;
      this._pointerId = e.pointerId;
      this._lastY = e.clientY;
      this.drifting = false;
      this.el.setPointerCapture?.(e.pointerId);
    });

    this._on(this.el, 'pointermove', (e) => {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      const dy = this._lastY - e.clientY;
      this._lastY = e.clientY;
      this.impulse += dy * this.cfg.dragScale;
    });

    const release = (e) => {
      if (e && this._pointerId !== null && e.pointerId !== this._pointerId) return;
      this._dragging = false;
      this._pointerId = null;
    };
    this._on(this.el, 'pointerup', release);
    this._on(this.el, 'pointercancel', release);
    this._on(globalThis, 'blur', () => {
      release();
      this.keys.clear();
    });

    this._on(globalThis, 'keydown', (e) => {
      if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(e.key)) {
        e.preventDefault();
      }
      if (e.key === 'Home') return this.jumpTo(0);
      if (e.key === 'End') return this.jumpTo(1);
      this.keys.add(e.key);
      if (this.keys.size) this.drifting = false;
    });
    this._on(globalThis, 'keyup', (e) => this.keys.delete(e.key));
  }

  jumpTo(t) {
    this.target = Math.min(1, Math.max(0, t));
    this.drifting = false;
  }

  update(dt) {
    const k = this.cfg.keyScale;
    let keyed = 0;
    if (this.keys.has('ArrowDown') || this.keys.has('s') || this.keys.has('S')) keyed += k;
    if (this.keys.has('ArrowUp') || this.keys.has('w') || this.keys.has('W')) keyed -= k;
    if (this.keys.has('PageDown')) keyed += k * 3;
    if (this.keys.has('PageUp')) keyed -= k * 3;

    this.target += this.impulse + keyed * dt;
    if (this.drifting) this.target += this.cfg.autoDrift * dt;
    this.impulse *= Math.pow(this.cfg.inertia, dt * 60);
    if (Math.abs(this.impulse) < 1e-6) this.impulse = 0;

    this.target = Math.min(1, Math.max(0, this.target));

    const prev = this.value;
    const lambda = 1 - Math.exp(-this.cfg.damping * dt);
    this.value += (this.target - this.value) * lambda;
    this.velocity = dt > 0 ? (this.value - prev) / dt : 0;

    if (!this.arrived && this.value > 0.9975) {
      this.arrived = true;
      this.bus.emit('arrive', { t: this.value });
    } else if (this.arrived && this.value < 0.985) {
      this.arrived = false;
    }

    return this.value;
  }

  dispose() {
    this._bound.forEach((off) => off());
    this._bound.length = 0;
  }
}
